<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row>
        <v-flex>
          <dt-search-box
            @typing="searchValue"
            @reset-datatable="resetDataTable()"
            @clear-search-field="search = null"
            @reset-advanced-search="resetAdvancedSearch()"
            advanced-search
          >
            <template v-slot:form="searchSlot">
              <form @submit.prevent="searchSlot.search">
                <v-layout row wrap>
                  <v-flex xs2 pa-2 v-if="hasPermissions(['outlet_select'])">
                    <v-autocomplete
                      v-model="outletId"
                      :label="$t('Outlet')"
                      hide-details
                      :items="outletsList"
                      data-vv-name="outlet"
                      :error="errors.has('outlet')"
                    />
                  </v-flex>

                  <v-flex xs2 pa-2>
                    <v-autocomplete
                      v-model="advancedSearch.select_date"
                      :items="dateFilter"
                      :label="`${$t('Select Date')}`"
                      auto-select-first
                      hide-details
                      :error="errors.has('Select Date')"
                      data-vv-name="Select Date"
                      :menu-props="{ transition: 'slide-y-transition' }"
                    />
                  </v-flex>

                  <v-flex xs2 pa-2 v-if="advancedSearch.select_date === 'DATE_PERIOD'">
                    <v-menu v-model="startDateMenu" full-width max-width="290" :close-on-content-click="false">
                      <v-text-field
                        slot="activator"
                        :value="advancedSearch.start_date"
                        :label="$t('Start Date')"
                        prepend-inner-icon="event"
                        readonly
                        hide-details
                        data-vv-name="start_date"
                        background-color="white"
                        autocomplete="off"
                        :error="errors.has('start_date')"
                        v-validate="'date_format:yyyy-MM-dd'"
                      />
                      <v-date-picker @change="startDateMenu = false" v-model="advancedSearch.start_date" />
                    </v-menu>
                  </v-flex>

                  <v-flex xs2 pa-2 v-if="advancedSearch.select_date === 'DATE_PERIOD'">
                    <v-menu v-model="endDateMenu" full-width max-width="290" :close-on-content-click="false">
                      <v-text-field
                        slot="activator"
                        :value="advancedSearch.end_date"
                        :label="$t('End Date')"
                        prepend-inner-icon="event"
                        readonly
                        hide-details
                        data-vv-name="end_date"
                        background-color="white"
                        autocomplete="off"
                        :error="errors.has('end_date')"
                        v-validate="'date_format:yyyy-MM-dd'"
                      />
                      <v-date-picker @change="endDateMenu = false" v-model="advancedSearch.end_date" />
                    </v-menu>
                  </v-flex>

                  <v-flex xs2 pa-2>
                    <v-text-field
                      v-model="advancedSearch.start_amount"
                      :label="$t('Start Amount')"
                      data-vv-name="start_amount"
                      hide-details
                      autocomplete="off"
                      :error="errors.has('start_amount')"
                      v-validate="'decimal:3|min_value:0'"
                      data-vv-delay="1000"
                    />
                  </v-flex>

                  <v-flex xs2 pa-2>
                    <v-text-field
                      v-model="advancedSearch.end_amount"
                      :label="$t('End Amount')"
                      data-vv-name="end_amount"
                      hide-details
                      autocomplete="off"
                      :error="errors.has('end_amount')"
                      v-validate="'decimal:3|min_value:0'"
                      data-vv-delay="1000"
                    />
                  </v-flex>

                  <v-flex xs2 pa-2>
                    <v-text-field
                      v-model="advancedSearch.payment_recipient"
                      :label="$t('Payment Recipient')"
                      data-vv-name="payment_recipient"
                      hide-details
                      autocomplete="off"
                      :error="errors.has('payment_recipient')"
                    />
                  </v-flex>

                  <v-flex xs2 pa-2>
                    <v-autocomplete
                      v-model="advancedSearch.expense_type_id_fk"
                      :items="expenseTypeList"
                      :label="`${$t('Expense Type')}`"
                      height="30px"
                      auto-select-first
                      hide-details
                      data-vv-name="expense type"
                      :menu-props="{ transition: 'slide-y-transition' }"
                    />
                  </v-flex>

                  <v-flex xs2 pa-2>
                    <v-autocomplete
                      v-model="advancedSearch.vender_id_fk"
                      :items="supplierList"
                      :label="`${$t('Supplier')}`"
                      height="30px"
                      auto-select-first
                      hide-details
                      data-vv-name="Supplier"
                      :menu-props="{ transition: 'slide-y-transition' }"
                    />
                  </v-flex>

                  <v-layout justify-end align-center>
                    <v-flex shrink>
                      <v-btn flat class="grey--text" @click="searchSlot.reset">
                        <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">clear</v-icon>
                        {{ $t('datatable-reset') }}
                      </v-btn>
                      <v-btn type="submit" color="success">
                        <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">filter_list</v-icon>
                        {{ $t('Filter') }}
                      </v-btn>
                    </v-flex>
                  </v-layout>

                  <v-flex xs12 pb-2 pt-3><v-divider /></v-flex>
                </v-layout>
              </form>
            </template>
          </dt-search-box>
        </v-flex>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-start align-end>
        <v-flex pa-2>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                style="margin:0 10px 0; height: 30px; width: 30px;"
                :loading="loadThePrintBtn"
                flat
                fab
                v-on="on"
                color="primary"
                @click="getNoPaginatedData('print')"
              >
                <v-icon>print</v-icon>
              </v-btn>
            </template>
            <span>{{ $t(`Print Table`) }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                style="margin: 0; height: 30px; width: 30px"
                fab
                :loading="loadTheExportBtn"
                flat
                v-on="on"
                color="primary"
                @click="getNoPaginatedData('export')"
              >
                <v-icon>fa-file-excel</v-icon>
              </v-btn>
            </template>
            <span>{{ $t(`Export Table`) }}</span>
          </v-tooltip>
        </v-flex>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.expense_id }}</td>-->
          <td>{{ props.item.expense_name }}</td>
          <td>{{ props.item.outlet_name }}</td>
          <td>{{ props.item.vender_name }}</td>
<!--          <td>{{ props.item.expense_number }}</td>-->
          <td>{{ props.item.voucher_number }}</td>
          <td>{{ props.item.amount | currency }}</td>
          <td>{{ props.item.payment_date }}</td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['expenditures_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item.expense_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Expense information`) }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <template v-slot:footer v-if="resultData.footer">
          <td :colspan="headers.length - 3">
            <strong>Total</strong>
          </td>
          <td colspan="3">
            <strong>{{ resultData.footer.total | currency }}</strong>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <view-expense :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    viewExpense: () => import('@/views/accounting/expenses/viewExpense.vue')
  },

  data() {
    return {
      advancedSearch: {},
      startDateMenu: false,
      endDateMenu: false,
      dialogs: {
        viewDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      outletId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'expense_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'expense_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Expense Type'), value: 'expense_name' },
        { text: this.$t('Outlet'), value: 'outlet_name' },
        { text: this.$t('Supplier'), value: 'vender_name' },
        // { text: this.$t('Expense Number'), value: 'expense_number' },
        { text: this.$t('Voucher Number'), value: 'voucher_number' },
        {
          text: `${this.$t('Amount')} (${localStorage.systemCurrency})`,
          value: 'amount'
        },
        { text: this.$t('Payment Date'), value: 'payment_date' }
      ],
      isFiltered: 0,
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Expenses Report'),
      icon: 'fa-chart-line'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }
    this.fetchOutletList()
    this.setExpenseTypesList()
    this.setVendersList()
  },

  computed: {
    ...mapGetters({
      resultData: 'expenses/getExpenseList',
      dateFilter: 'expenses/getDateFilter',
      outletsList: 'outlets/getFullReportOutletsList',
      expenseTypeList: 'expenseTypes/getFullExpenseTypesList',
      supplierList: 'suppliers/getFullVendersList'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      deep: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          p.sortBy = p.sortBy != null ? p.sortBy : 'expense_id'
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'expenses/updateReportExpenseList',
      fetchExpenseInfo: 'expenses/fetchExpenseInfo',
      fetchOutletList: 'outlets/setFullOutletsList',
      setExpenseTypesList: 'expenseTypes/setFullExpenseTypesList',
      setVendersList: 'suppliers/setFullVendersList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions(['expenditures_show'])
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get(`report_expenses/${this.outletId}`, {
        params: {
          is_paginated: 0,
          search: this.search,
          ...this.advancedSearch,
          isFiltered: this.isFiltered,
          outlet_id: this.outletId
        }
      })
        .then(response => {
          const resultData = response.data.data
          let resultWithFooter = resultData.data
          resultWithFooter.footer = resultData.footer
          this.tableBody = resultWithFooter
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        ...this.pagination,
        search: this.search,
        id: this.outletId,
        advancedSearch: this.advancedSearch,
        isFiltered: this.isFiltered
      }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
      this.advancedSearch = {}
      this.outletId = 0
      this.isFiltered = 0
    },

    resetAdvancedSearch() {
      this.advancedSearch = {}
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.isFiltered = 1
      this.search = val

      if (this.search === this.oldSearch) {
        this.updateDatatableResult()
      }
    },

    viewRecord(id) {
      this.fetchExpenseInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    }
  }
}
</script>

<style scoped />
